import StarSVG from '@public/icons/star.svg';
import classNames from 'classnames';
import { AnimatePresence, Variants, m as motion } from 'framer-motion';
import Image from 'next/image';
import ArrSVG from 'public/icons/btn-arr.svg';
import React, { useEffect, useState } from 'react';
import { TransformedCard } from '@/shared/api/lib/utils/transform-program';
import { useFavoriteProgramsSetState, useFavoriteProgramsValue } from '@/shared/atoms/favorite-programm';
import { useSetFavoriteWindowState } from '@/shared/atoms/favorite-window';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Tooltip from '@/shared/components/shared/Tooltip';
import { DICT } from '@/shared/config/dict';
import { useLocale } from '@/shared/hooks/use-locale';
import { ImageShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { easeOutQuart } from '@/shared/lib/utils/easings';
import ProgramDetailPopup from '../../popups/ProgramDetailPopup';
import { PROGRAM_DETAIL_POPUP } from '../../popups/ProgramDetailPopup/ProgramDetailPopup';
import SchemaPopup, { SCHEMA_POPUP_NAME } from '../../popups/SchemaPopup/SchemaPopup';
import Button from '../../shared/Button';
import FavoriteBtn from '../../shared/FavoriteBtn';
import FavoriteWindow from '../../shared/FavoriteWindow';
import LogoSlider from '../../shared/LogoSlider';
import { ParticipationFormatsCircles } from '../../shared/ParticipationFormatsCircles';
import Typografed from '../../shared/Typografed';

type Props = {
    cards: TransformedCard[];
    isDayVisible?: boolean;
    isButtonVisible?: boolean;
    isFetching: boolean;
    visibleCardsCount: number;
    disableDirections: boolean;
    mainTheme?: string;
    pdfTitle?: string;
};

const initialItem: TransformedCard = {
    id: 0,
    slug: '',
    time: '',
    name: '',
    day: '',
    place: undefined,
    directions: '',
    participationFormats: [],
    participationDescription: '',
};

const variants: Variants = {
    hidden: {
        y: 10,
        opacity: 0,
    },
    visible: {
        y: 0,
        opacity: 1,
    },
};

function ProgramDetailList({
    cards,
    isButtonVisible,
    isDayVisible,
    isFetching,
    visibleCardsCount,
    disableDirections,
    mainTheme,
    pdfTitle,
}: Props) {
    const favorites = useFavoriteProgramsValue();
    const setFavorites = useFavoriteProgramsSetState();

    const [showAll, setShowAll] = useState(false);
    const [activeItem, setActiveItem] = useState<TransformedCard | undefined>(undefined);

    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const isMobile = device.includes('mobile');

    const { openPopup } = useSetOpenedPopupsState();
    const setFavoriteWindow = useSetFavoriteWindowState();
    const visibleCards = showAll ? cards : cards.slice(0, visibleCardsCount);
    useEffect(() => {
        if (!isMobile) {
            setFavoriteWindow(favorites.length > 0);
        }
    }, [favorites, setFavoriteWindow, isMobile]);

    const toggleFavorite = (id: number) => {
        cards.find((item) => {
            if (item.id === id) {
                if (favorites.includes(item)) {
                    setFavorites(favorites.filter((favId) => favId.id !== id));
                } else {
                    setFavorites([...favorites, item]);
                }
            }
        });
    };

    const handleCardClick = (card: TransformedCard) => {
        setActiveItem(card);
        if (card.description) {
            openPopup(PROGRAM_DETAIL_POPUP);
        }
    };

    const handlePlaceClick = (card: TransformedCard) => {
        setActiveItem(card);
        if (card.place?.image) {
            openPopup(SCHEMA_POPUP_NAME);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                variants={variants}
                initial="hidden"
                animate={isFetching ? 'hidden' : 'visible'}
                exit="hidden"
                transition={{ duration: 0.5, ease: easeOutQuart }}
                className="program-detail-list"
            >
                {visibleCards.map((card) => (
                    <div
                        key={card.id}
                        role="button"
                        tabIndex={0}
                        className="program-detail-list-row"
                        onClick={() => handleCardClick(card)}
                    >
                        <CardDateAndPlace card={card} onPlaceClick={handlePlaceClick} isDayVisible={isDayVisible} />

                        <div className="program-detail-list-row__main-cols">
                            <CardTopic card={card} />
                            <CardParticipationsFormat card={card} />
                            <CardLogo card={card} />
                        </div>

                        <CardActions
                            card={card}
                            toggleFavorite={toggleFavorite}
                            isFavorite={favorites.some((item) => item.id === card.id)}
                        />
                    </div>
                ))}

                {!showAll && cards.length > visibleCardsCount && (
                    <div className="program-detail-list-btn-wrapper">
                        <Button
                            className="program-detail-list-btn"
                            variant="outline-dark"
                            onClick={() => setShowAll(true)}
                        >
                            Показать все
                        </Button>
                    </div>
                )}
                <FavoriteWindow favorites={favorites} pdfTheme={mainTheme || ''} pdfTitle={pdfTitle} />
                <AnimatePresence>
                    {favorites.length > 0 && isMobile && isButtonVisible && (
                        <FavoriteBtn isScrollButtonVisible={isButtonVisible} />
                    )}
                </AnimatePresence>

                <ProgramDetailPopup program={activeItem || initialItem} disableDirections={disableDirections} />
                {activeItem && activeItem.place && (
                    <SchemaPopup image={activeItem.place.image} content={{ name: activeItem.place.name }} isBackBtn />
                )}
            </motion.div>
        </AnimatePresence>
    );
}

type CardDateAndPlaceProps = {
    isDayVisible?: boolean;
    card: TransformedCard;
    onPlaceClick: (card: TransformedCard) => void;
};
const CardDateAndPlace = ({ isDayVisible, card, onPlaceClick }: CardDateAndPlaceProps) => {
    const handlePlaceClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onPlaceClick(card);
    };

    return (
        <div className="program-detail-list-row-date text-s">
            {isDayVisible && <p>{card.day}</p>}

            <p className="program-detail-list-row-date__time">{card.time}</p>

            {card.place && (
                <button className="program-detail-list-row-place" onClick={handlePlaceClick}>
                    <Typografed>{card.place.name}</Typografed>
                </button>
            )}
        </div>
    );
};

const CardTopic = ({ card }: { card: TransformedCard }) => {
    return (
        <div className="program-detail-list-row-topic">
            {card.prefix && (
                <div className="text-opacity text-s">
                    <Typografed>{card.prefix}</Typografed>
                </div>
            )}
            <div className="program-detail-list-row-topic__name text-m">
                <Typografed>{card.name}</Typografed>
            </div>
        </div>
    );
};

const CardParticipationsFormat = ({ card }: { card: TransformedCard }) => {
    const locale = useLocale();

    return (
        <div className="program-detail-list-row-format">
            {card.participationFormats.length > 0 ? (
                <>
                    <div className="text-opacity text-s">{tp(DICT.PARTICIPATION_FORMAT[locale])}:</div>
                    <ParticipationFormatsCircles formats={card.participationFormats} />
                </>
            ) : (
                card.participationDescription &&
                card.participationDescription?.length > 0 && (
                    <>
                        <div className="text-opacity text-s">{tp(DICT.PARTICIPATION_FORMAT[locale])}:</div>
                        <div className="text-s">{tp(card.participationDescription)}</div>
                    </>
                )
            )}
        </div>
    );
};

const CardLogo = ({ card }: { card: TransformedCard }) => {
    const companies = card.companies?.filter((company): company is ImageShape => Boolean(company?.src)) ?? [];

    return (
        <div className="program-detail-list-row-logo">
            {companies.length > 1 ? (
                <div className="program-detail-list-row-logo__slider">
                    <LogoSlider images={companies} />
                </div>
            ) : (
                companies.length === 1 && (
                    <Image src={companies[0].src} alt={companies[0].alt || ''} width={200} height={100} />
                )
            )}
        </div>
    );
};

type CardActionsProps = {
    card: TransformedCard;
    isFavorite: boolean;
    toggleFavorite: (id: TransformedCard['id']) => void;
};

const CardActions = ({ card, isFavorite, toggleFavorite }: CardActionsProps) => {
    return (
        <div className="program-detail-list-row-right">
            <Tooltip message="Добавить в избранное">
                <button
                    className={classNames('program-detail-list-row-star', {
                        'is-favorite': isFavorite,
                    })}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(card.id);
                    }}
                    aria-label="Добавить в избранное"
                >
                    <StarSVG />
                </button>
            </Tooltip>

            <div
                className={classNames('program-detail-list-row-button', {
                    'is-opacity': !card.description,
                })}
            >
                <ArrSVG />
            </div>
        </div>
    );
};

export default ProgramDetailList;
